import React, { useEffect, useState } from "react";
import axios from "axios";
import { Star, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StarRow = ({ value, onChange, size = 20 }) => (
  <div className="flex items-center gap-1">
    {Array.from({ length: 5 }).map((_, i) => {
      const n = i + 1;
      const filled = value >= n;
      return (
        <button
          type="button"
          key={n}
          onClick={() => onChange?.(n)}
          className={onChange ? "cursor-pointer" : "cursor-default"}
          data-testid={`star-${n}`}
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
        >
          <Star size={size} className={filled ? "text-rose" : "text-off-white/20"} fill={filled ? "currentColor" : "none"} />
        </button>
      );
    })}
  </div>
);

const ProductReviews = ({ productId }) => {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState({ count: 0, average: null, reviews: [] });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/reviews`, { params: { productId } });
      setData(data);
    } catch (_e) { /* noop */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [productId]);// eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, { productId, rating, title, body }, { withCredentials: true });
      toast.success("Thanks! Your review is pending approval.");
      setShowForm(false); setTitle(""); setBody(""); setRating(5);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to submit review");
    } finally { setSubmitting(false); }
  };

  return (
    <section className="py-16 border-t border-off-white/10" data-testid="product-reviews">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-2">// VERIFIED REVIEWS</p>
          <h2 className="font-serif-display italic text-3xl md:text-4xl text-off-white">
            {data.average ? `${data.average}/5` : "No reviews yet"}
          </h2>
          {data.count > 0 && (
            <div className="flex items-center gap-3 mt-3">
              <StarRow value={Math.round(data.average || 0)} />
              <span className="font-mono text-[10px] tracking-[0.25em] text-off-white/50">{data.count} REVIEW{data.count !== 1 ? "S" : ""}</span>
            </div>
          )}
        </div>
        {!authLoading && (
          user ? (
            <button
              onClick={() => setShowForm((s) => !s)}
              className="h-11 px-5 bg-rose text-charcoal font-mono text-[11px] tracking-[0.25em] font-semibold hover:bg-off-white transition-colors-only"
              data-testid="write-review-btn"
            >
              {showForm ? "CANCEL" : "WRITE A REVIEW"}
            </button>
          ) : (
            <a
              href="/login"
              className="h-11 px-5 border border-off-white/20 hover:border-rose hover:text-rose font-mono text-[11px] tracking-[0.25em] text-off-white/80 flex items-center transition-colors-only"
              data-testid="signin-to-review"
            >
              SIGN IN TO REVIEW
            </a>
          )
        )}
      </div>

      {showForm && user && (
        <form onSubmit={submit} className="mb-10 border border-off-white/10 bg-off-white/[0.02] p-6 space-y-4" data-testid="review-form">
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] text-off-white/60 mb-2">YOUR RATING</p>
            <StarRow value={rating} onChange={setRating} />
          </div>
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] text-off-white/60 mb-2">HEADLINE (OPTIONAL)</p>
            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80}
              className="w-full h-11 bg-off-white/[0.04] border border-off-white/20 focus:border-rose outline-none px-4 text-off-white transition-colors-only"
              data-testid="review-title-input"
            />
          </div>
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] text-off-white/60 mb-2">YOUR REVIEW</p>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} required rows={4} maxLength={800}
              className="w-full bg-off-white/[0.04] border border-off-white/20 focus:border-rose outline-none px-4 py-3 text-off-white transition-colors-only"
              data-testid="review-body-input"
            />
          </div>
          {error && <p className="font-mono text-[11px] tracking-[0.2em] text-rose-deep">// {String(error).toUpperCase()}</p>}
          <button
            type="submit" disabled={submitting || !body.trim()}
            className="h-12 px-6 bg-rose text-charcoal font-mono text-[11px] tracking-[0.25em] font-semibold hover:bg-off-white transition-colors-only disabled:opacity-60"
            data-testid="submit-review-btn"
          >
            {submitting ? "SUBMITTING…" : "SUBMIT REVIEW"}
          </button>
          <p className="font-mono text-[9px] tracking-[0.25em] text-off-white/40">// REVIEWS ARE VERIFIED-PURCHASE ONLY · APPROVED BY WHIP IT UP BEFORE PUBLISHING</p>
        </form>
      )}

      {loading ? (
        <div className="flex items-center gap-3 text-off-white/50 py-6"><Loader2 size={16} className="animate-spin text-rose" /> Loading reviews…</div>
      ) : data.reviews.length === 0 ? (
        <p className="text-off-white/50 py-6">Be the first to leave a review.</p>
      ) : (
        <ul className="space-y-4">
          {data.reviews.map((r, i) => (
            <li key={i} className="border border-off-white/10 bg-off-white/[0.02] p-5" data-testid={`review-item-${i}`}>
              <div className="flex items-center gap-3">
                <StarRow value={r.rating} size={14} />
                <span className="font-mono text-[10px] tracking-[0.25em] text-off-white/50">{r.user_name}</span>
                <span className="font-mono text-[10px] tracking-[0.25em] text-off-white/30">
                  {r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}
                </span>
              </div>
              {r.title && <p className="mt-2 font-serif-display italic text-xl text-off-white">{r.title}</p>}
              <p className="mt-1 text-off-white/80">{r.body}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default ProductReviews;
